import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { addDays } from 'date-fns';
import { useSnackbar } from 'notistack';

import { Dialog } from '@components/dialog';
import { DatePicker } from '@components/datePicker/DatePicker';
import { ToastType } from '@components/toast/ToastType.enum';
import { TQuoteApiResponse } from '@models/quotes';
import { useConvertRenewalToNewBusiness } from '@services/quotes';
import { fakeLocalMidnightFromTZMidnight, setDateToMidnight } from '@utils/date';
import { getApiErrorMessage } from '@utils/getApiErrorMessage';

interface Props {
  quote: TQuoteApiResponse;
  handleClose: () => void;
}

export const ConvertRenewalToNewBusinessDialog = ({ quote, handleClose }: Props) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { convertRenewalToNewBusiness, isPending } = useConvertRenewalToNewBusiness();
  const [effectiveOn, setEffectiveOn] = useState<Date | null>(null);

  const quoteEffectiveOn = new Date(quote.effectiveOn);
  const minDate = fakeLocalMidnightFromTZMidnight(addDays(quoteEffectiveOn, 1));
  const maxDate = fakeLocalMidnightFromTZMidnight(addDays(quoteEffectiveOn, 120));

  const handleSubmit = async () => {
    if (!effectiveOn) return;

    try {
      const result = await convertRenewalToNewBusiness({
        sourceQuoteId: quote.id,
        effectiveOn: setDateToMidnight(effectiveOn),
      });
      handleClose();
      navigate(`/quote/${result.id}`);
      enqueueSnackbar('Quote successfully converted to New Business', { variant: ToastType.Success });
    } catch (e) {
      const message = await getApiErrorMessage(e, 'Failed to convert renewal to new business');
      enqueueSnackbar(message, { variant: ToastType.Error, persist: true });
    }
  };

  return (
    <Dialog
      open
      title='Convert Renewal to New Business'
      primaryBtnText='Convert to New Business'
      primaryBtnAction={handleSubmit}
      isPrimaryBtnDisabled={!effectiveOn || isPending}
      isPrimaryBtnLoading={isPending}
      secondaryBtnText='Exit'
      secondaryBtnAction={handleClose}
      handleCloseDialog={handleClose}
      size='small'
    >
      <Box sx={{ padding: '24px 28px 8px' }}>
        <Typography variant='body2' sx={{ mb: 3 }}>
          This action is to allow for a gap in coverage between the expiring policy and the new &ldquo;renewal.&rdquo;
          Performing this action will result in a new policy number. The new policy will stay linked to the old policy
          for reference.
        </Typography>
        <DatePicker
          value={effectiveOn}
          onChange={(date) => setEffectiveOn(date)}
          minDate={minDate}
          maxDate={maxDate}
          referenceDate={minDate}
          textFieldProps={{ inputLabel: 'Effective Date' }}
        />
      </Box>
    </Dialog>
  );
};
